// utils/formatDate.js
import { format } from "date-fns";

/**
 * Get user's date format preference from localStorage
 * @returns {string} Date format string
 */
export const getUserDateFormat = () => {
  try {
    const appSetting = JSON.parse(localStorage.getItem('appSettings') || '{}');
    return appSetting.dateFormat || 'DD/MM/YYYY'; // Default fallback
  } catch (error) {
    console.error('Error reading date format from localStorage:', error);
    return 'DD/MM/YYYY'; // Default fallback
  }
};

/**
 * Get user's time format preference from localStorage
 * @returns {string} Time format string
 */
export const getUserTimeFormat = () => {
  try {
    const appSetting = JSON.parse(localStorage.getItem('appSettings') || '{}');
    return appSetting.timeFormat || '12hr'; // Default fallback
  } catch (error) {
    console.error('Error reading time format from localStorage:', error);
    return '12hr'; // Default fallback
  }
};

/**
 * Convert user date format to date-fns format string
 * @param {string} userFormat - User's preferred date format (DD/MM/YYYY, MM/DD/YYYY, YYYY/MM/DD)
 * @returns {string} date-fns compatible format string
 */
export const convertToDateFnsFormat = (userFormat) => {
  const formatMap = {
    'DD/MM/YYYY': 'dd/MM/yyyy',
    'MM/DD/YYYY': 'MM/dd/yyyy',
    'YYYY/MM/DD': 'yyyy/MM/dd',
    'DD-MM-YYYY': 'dd-MM-yyyy',
    'MM-DD-YYYY': 'MM-dd-yyyy',
    'YYYY-MM-DD': 'yyyy-MM-dd',
    'DD.MM.YYYY': 'dd.MM.yyyy',
    'MM.DD.YYYY': 'MM.dd.yyyy',
    'YYYY.MM.DD': 'yyyy.MM.dd',
    'YYYY.MMM.DD': 'yyyy.mmm.dd',
    'DD MMM YYYY': 'dd MMM yyyy',
  };

  return formatMap[userFormat] || 'dd/MM/yyyy'; // Default fallback
};

/**
 * Get time format string based on user preference
 * @param {string} timeFormat - User's preferred time format (12hr or 24hr)
 * @returns {string} date-fns compatible time format string
 */
export const getTimeFormatString = (timeFormat) => {
  return timeFormat === '24hr' ? 'HH:mm' : 'hh:mm a';
};

/**
 * Format date according to user's preferences
 * @param {Date|string} date - Date to format
 * @param {boolean} includeTime - Whether to include time in the format
 * @param {boolean} longFormat - Whether to use long format (with day name, etc.)
 * @returns {string} Formatted date string
 */
export const formatDate = (date, includeTime = false, longFormat = false) => {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '';

    const userDateFormat = getUserDateFormat();
    const userTimeFormat = getUserTimeFormat();

    const dateFnsFormat = convertToDateFnsFormat(userDateFormat);
    const timeFormatString = getTimeFormatString(userTimeFormat);

    if (longFormat) {
      // Long format: "Monday, 25/12/2023 at 2:30 PM"
      const dayFormat = 'EEEE';
      const atText = includeTime ? ' at ' : '';
      const timeFormat = includeTime ? timeFormatString : '';
      return format(dateObj, `${dayFormat}, ${dateFnsFormat}${atText}${timeFormat}`);
    } else if (includeTime) {
      // Short format with time: "25/12/2023 2:30 PM"
      return format(dateObj, `${dateFnsFormat} ${timeFormatString}`);
    } else {
      // Date only: "25/12/2023"
      return format(dateObj, dateFnsFormat);
    }
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Format date for display in tables/lists (compact format)
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateCompact = (date) => {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '';

    const userDateFormat = getUserDateFormat();
    const userTimeFormat = getUserTimeFormat();

    // Use shorter month format for compact display
    let compactFormat;
    switch (userDateFormat) {
      case 'DD/MM/YYYY':
        compactFormat = 'dd MMM yyyy';
        break;
      case 'MM/DD/YYYY':
        compactFormat = 'MMM dd, yyyy';
        break;
      case 'YYYY/MM/DD':
        compactFormat = 'yyyy MMM dd';
        break;
      default:
        compactFormat = 'dd MMM yyyy';
    }

    const timeFormatString = getTimeFormatString(userTimeFormat);
    return format(dateObj, `${compactFormat} ${timeFormatString}`);
  } catch (error) {
    console.error('Error formatting compact date:', error);
    return '';
  }
};

/**
 * Format date for date picker display button
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateForPicker = (date) => {
  if (!date) return 'Select date';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return 'Select date';

    const userDateFormat = getUserDateFormat();

    // Always use abbreviated month format for date picker
    let pickerFormat;
    switch (userDateFormat) {
      case 'DD/MM/YYYY':
        pickerFormat = 'dd MMM yyyy';
        break;
      case 'MM/DD/YYYY':
        pickerFormat = 'MMM dd, yyyy';
        break;
      case 'YYYY/MM/DD':
        pickerFormat = 'yyyy MMM dd';
        break;
      default:
        pickerFormat = 'dd MMM yyyy';
    }

    return format(dateObj, pickerFormat);
  } catch (error) {
    console.error('Error formatting date for picker:', error);
    return 'Select date';
  }
};

/**
 * Format time only according to user's preference
 * @param {Date|string} date - Date to extract time from
 * @returns {string} Formatted time string
 */
const isRealCalendarDate = (year, month, day) => {
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1970) return false;
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
};

/**
 * Parse a trip-report date using the org date format.
 * Report dates arrive as DD/MM/YYYY, MM/DD/YYYY, or YYYY/MM/DD.
 * Treating every slash date as DD/MM rolls invalid months forward
 * (10/15/2026 becomes 10 March 2027) and the path query then misses the GPS.
 */
export const parseReportDate = (dateStr) => {
  if (!dateStr) return null;
  const raw = String(dateStr).trim();

  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const year = Number(iso[1]);
    const month = Number(iso[2]);
    const day = Number(iso[3]);
    if (!isRealCalendarDate(year, month, day)) return null;
    return { year, month, day, date: new Date(year, month - 1, day) };
  }

  const parts = raw.split(/[/.]/).map((part) => part.trim());
  if (parts.length < 3) return null;
  const nums = parts.slice(0, 3).map((part) => parseInt(part, 10));
  if (nums.some((value) => Number.isNaN(value))) return null;

  const format = getUserDateFormat();
  let year;
  let month;
  let day;
  if (String(parts[0]).length === 4 || format === "YYYY/MM/DD") {
    [year, month, day] = nums;
  } else if (format === "MM/DD/YYYY") {
    [month, day, year] = nums;
  } else {
    [day, month, year] = nums;
  }

  if (!isRealCalendarDate(year, month, day)) return null;
  return { year, month, day, date: new Date(year, month - 1, day) };
};

/** `/location/all` always expects DD/MM/YYYY. */
export const toDdMmYyyy = (dateStr) => {
  const parsed = parseReportDate(dateStr);
  if (!parsed) return "";
  const dd = String(parsed.day).padStart(2, "0");
  const mm = String(parsed.month).padStart(2, "0");
  return `${dd}/${mm}/${parsed.year}`;
};

export const formatTime = (date) => {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '';

    const userTimeFormat = getUserTimeFormat();
    const timeFormatString = getTimeFormatString(userTimeFormat);

    return format(dateObj, timeFormatString);
  } catch (error) {
    console.error('Error formatting time:', error);
    return '';
  }
};

// Export all functions for easy importing
export default {
  formatDate,
  formatDateCompact,
  formatDateForPicker,
  formatTime,
  getUserDateFormat,
  getUserTimeFormat,
  convertToDateFnsFormat,
  getTimeFormatString,
  parseReportDate,
  toDdMmYyyy,
};